import { ApiErrorMessages, GTMEvents } from '../../constants';
import { ApplicationData, AppWorkspaceData, GTMEventsInterface } from '../../interfaces';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { DatasetSource, Hit, ElasticApiResponse, trulyHasData, UserRoleMap, OrganizationsListResponse, OrganizationData, DropDownInterface, PollyMessageService } from 'polly-commons';
import {
  NEW_WORKSPACE_DROPDOWN_OPTION,
  WORKSPACE_NAME_MAX_LENGTH,
  WorkspaceResource,
  WorkspaceResponse,
  RepositoryResource,
  DatasetSourceV2
} from 'polly-commons';
import { PollyManageAppLaunchService } from '../../services';
import { SubSink } from 'subsink';
import { PollyManageAnalyticsService, PollyManageBackendService, PollyManageDataService } from '../../../../@core/services';
import { MixpanelPropertiesInterface } from '../../../../@core/interfaces/analytics.interface';
import { FilterParam } from '../../../../@core/interfaces/elastic-query.interface';
import { buildWorkspaceListingQuery, convertESResponseToWorkspaceResponse } from '../../../../@core/utilities/helper-functions/workspaces.utilities';
import { ES_WORKSPACES_ENDPOINT } from '../../../workspace-module/polly-manage-workspace.constants';
import { ESWorkspaceAttributes } from '../../../../@core/interfaces';
import { RepositoryResourceV2 } from '../../../omixatlas-module/@core/interfaces';
interface WorkspaceItem {
  name: string;
  id: number;
  value: WorkspaceResource | null;
}

@Component({
  selector: 'app-polly-manage-analysis-config',
  templateUrl: './polly-manage-analysis-config.component.html',
  styleUrls: ['./polly-manage-analysis-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PollyManageAnalysisConfigComponent implements OnInit, OnDestroy, OnChanges {
  public loadingMessage: string | undefined | null;
  public selectedClientName: string | undefined;
  public searchedClientName: string | undefined;
  public submitAttempt: number = 0;
  public showModal: boolean = true;
  public isInternalOrgUser: boolean = false;
  public subsink: SubSink = new SubSink();
  public clientOrgList: DropDownInterface[] = [];
  public workspaceList: WorkspaceItem[] = [];
  public response: AppWorkspaceData = {
    workspace: {
      id: null,
      name: null,
      value: null,
    },
    analysis: {
      name: null,
      description: null,
    },
    client: {
      label: null,
      value: null
    },
    notebook_name: null
  };
  public GTMEvents: GTMEventsInterface = GTMEvents;
  public maxInputLength: number = WORKSPACE_NAME_MAX_LENGTH;
  public readonly NEW_WORKSPACE_DROPDOWN_OPTION: string = NEW_WORKSPACE_DROPDOWN_OPTION;
  @Input() public workspaceSelectorConfig: ApplicationData | undefined;
  @Input() public isSelectedWorkspace: boolean = false;
  @Input() public repoID!: string;
  @Input() public datasetInfo: Hit<DatasetSource> | Hit<DatasetSourceV2> | undefined;
  @Input() public readonly currentRepository: RepositoryResource | RepositoryResourceV2 | undefined;
  @Input() public filterStatus!: FilterParam;
  @Input() public applicationTitle!: string | undefined;
  @Input() public currentWorkspace: WorkspaceResource | undefined;
  @Input() public showLoader: boolean = false;
  @Input() public launchSource: string | undefined;
  @Input() public readonly currentRepoV2: RepositoryResourceV2 | undefined;
  @Output() public setConfig: EventEmitter<AppWorkspaceData> = new EventEmitter();
  @Output() public closeComponent: EventEmitter<boolean> = new EventEmitter();
  @Output() public gtmEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() public createWorkspace: EventEmitter<WorkspaceResponse> = new EventEmitter<WorkspaceResponse>();
  private readonly subSink: SubSink = new SubSink();
  private readonly messageService = inject(PollyMessageService);

  constructor(
    private readonly appLaunchService: PollyManageAppLaunchService,
    private readonly backendService: PollyManageBackendService,
    private readonly dataService: PollyManageDataService,
    private readonly manageAnalyticsService: PollyManageAnalyticsService,
    ) {}

  public ngOnInit(): void {
    this.getUserInfoType();
    this.setWorkspaceOptions();
    this.setDefaultAnalysisConfig();

  }

  public getUserInfoType(): void {
    const userRoles: UserRoleMap | null = this.dataService.getCurrentUserRoles();
    if (userRoles && userRoles['ELUCIDATA_DEFAULT_ROLE']) {
      this.isInternalOrgUser = true;
      this.fetchOrganizationsList();
    }
  }

  public fetchOrganizationsList(size?: number): void {
    this.subSink.sink = this.backendService.searchOrganizations(this.searchedClientName, size)
    .subscribe({
      next: (orgListResponse: OrganizationsListResponse) => {
        this.clientOrgList = orgListResponse.data.map((org: OrganizationData) => {
          return {
            label: org.attributes.name,
            value: org.id
          };
        })
      },
      error: (_) => {
        this.messageService.showCustomToast({ severity: 'error', summary: 'Failure', detail: 'Something went wrong. Please try again later.', closable: false });
      }
    });
  }

  public onOrgSelection(selectEventValue: { label: string, value: string }): void {
    this.response.client = selectEventValue;
    this.selectedClientName = selectEventValue.label;
  }

  public onClientChange(event: string | DropDownInterface): void {
    if (typeof event === 'string') {
      const org = this.clientOrgList.find(org => org.label === event);
      this.selectedClientName = org ? org.label : undefined;
    }
  }

  public searchOrganizations(event: { query: string }): void {
    if (event.query && event.query.length >= 3) {
      if (this.searchedClientName === encodeURI(event.query)) {
        const list = this.clientOrgList;
        this.clientOrgList = [...list];
      } else {
        this.searchedClientName = encodeURI(event.query);
        this.fetchOrganizationsList(50);
      }
    } else {
      this.searchedClientName = undefined;
      this.fetchOrganizationsList(50);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.showLoader) {
      this.showLoader = changes.showLoader.currentValue;
    }
  }

  /**
   * If selectedWorkspace is true, means we've to make Workspace dropdown readonly, so the cached Workspaces list will do.
   * Else We have to fetch the Workspaces list from API
   */
  private setWorkspaceOptions(): void {
    this.loadingMessage = 'Fetching Workspaces';
    if (this.isSelectedWorkspace && this.currentWorkspace) {
      const { name, id } = this.currentWorkspace.attributes;
      this.response.workspace = { name, id, value: this.currentWorkspace };
      this.workspaceList.push({ name, id, value: this.currentWorkspace });
      this.loadingMessage = undefined;
    } else {
      this.getAllWorkspaces();
    }
  }

  private getAllWorkspaces(): void {
    const body = buildWorkspaceListingQuery(0, {}, {showActive: true}, 1000, true);
    const currentUser = this.dataService.getCurrentUser();
    const currentUserId = (currentUser && Number(currentUser.id)) || -1;
    // Get workspaces from ES
    this.subSink.sink = this.backendService
      .fetchWorkspacesFromES(ES_WORKSPACES_ENDPOINT, body)
      .pipe<ElasticApiResponse<ESWorkspaceAttributes, undefined>>(trulyHasData)
      .subscribe((workspaceList: ElasticApiResponse<ESWorkspaceAttributes, undefined>) => {
        this.workspaceList = workspaceList.hits.hits.map((workspace) => {
          const workspaceObj = convertESResponseToWorkspaceResponse(workspace._source, currentUserId);
          return { name: workspace._source.name, id: workspace._source.id, value: workspaceObj };
        });
        this.workspaceList.unshift({ name: this.NEW_WORKSPACE_DROPDOWN_OPTION, id: -1, value: null});
        this.loadingMessage = null;
      },
      () => (this.loadingMessage = 'Failed to fetch Workspaces. Please try again')
      );
  }

  /**
   * Setting default values for the name and description of analysis
   */
  public setDefaultAnalysisConfig(): void {
    if (this.workspaceSelectorConfig && this.workspaceSelectorConfig.notebookName && this.workspaceSelectorConfig.notebookName.value) {
      this.response.notebook_name = this.workspaceSelectorConfig.notebookName.value;
    }
    if (!this.workspaceSelectorConfig || !this.workspaceSelectorConfig.input || !this.workspaceSelectorConfig.input.name) return;
    if (this.workspaceSelectorConfig.input.name.value) this.response.analysis.name = this.workspaceSelectorConfig.input.name.value;
    if (this.workspaceSelectorConfig.input.description && this.workspaceSelectorConfig.input.description.value) {
      this.response.analysis.description = this.workspaceSelectorConfig.input.description.value;
    }
  }

  /**
   * Unsubscribe to all subscriptions
   */
  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  /**
   * This function will validate the form and set analysis config
   * if validation is successful else raise error
   */
  public setAnalysisConfig(): void {
    this.submitAttempt++;
    if (this.isInternalOrgUser && !this.response.client.label && !this.response.client.value) return
    if (this.workspaceList && this.workspaceList.length && !this.response.workspace.id) return;
    if (
      this.workspaceSelectorConfig &&
      this.workspaceSelectorConfig.input &&
      this.workspaceSelectorConfig.input.name &&
      this.workspaceSelectorConfig.input.name.required &&
      !this.verifyAnalysisName()
    ) {
      return;
    }
    if (
      this.workspaceSelectorConfig && this.workspaceSelectorConfig.notebookName && this.workspaceSelectorConfig.notebookName.required &&
      !this.response.notebook_name
    ) {
      return;
    }
    if (this.workspaceSelectorConfig && this.workspaceSelectorConfig.input && this.workspaceSelectorConfig.input.description) {
      if (this.workspaceSelectorConfig.input.description.required && !this.response.analysis.description) return;
    }
    if (this.response.workspace.name === this.NEW_WORKSPACE_DROPDOWN_OPTION && !this.response.new_workspace_name) return;
    if (this.response.workspace.name === this.NEW_WORKSPACE_DROPDOWN_OPTION && this.response.new_workspace_name) {
      this.createNewWorkspace();
    } else {
      this.setConfig.emit(this.response);
      this.showLoader = true;
    }
  }

  public createNewWorkspace(): void {
    if (!this.response.new_workspace_name) return;
    this.showLoader = true;
    this.subSink.sink = this.appLaunchService
      .createWorkspace(this.response.new_workspace_name)
      .pipe(finalize(() => (this.showLoader = false)))
      .subscribe(
        (workspaceResponse: WorkspaceResponse): void => {
          this.createWorkspace.emit(workspaceResponse);
          this.response.workspace = {
            id: workspaceResponse.data.attributes.id,
            name: workspaceResponse.data.attributes.name,
            value: workspaceResponse.data,
          };
          this.setConfig.emit(this.response);
          this.showLoader = true;
        },
        (_: Error) => {
          this.messageService.showToast('Failure', ApiErrorMessages.WORKSPACE_CREATION_FAILURE_MESSAGE, 'error');
        }
      );
  }
  /**
   * This function send the data to Analytics
   * @param data used for Analytics
   */
  public sendAnalyticsEvent(data: string): void {
    this.gtmEvent.emit(data);
  }
  /**
   * This function send the data to Analytics
   * @param data used for Analytics
   */
  public sendMixpanelEvent(data: string): void {
    const mixpanelProperties: MixpanelPropertiesInterface = {
      Dataset_Id: this.datasetInfo ? this.datasetInfo._source.dataset_id : '',
      Repository_Id: this.repoID,
      Repository_Name: this.currentRepository ? this.currentRepository.attributes.repo_name : '',
      Workspace_Id: this.response.workspace.id,
      Component_Name: this.applicationTitle
    };

    if (this.response.workspace.value && this.response.workspace.id){
      this.manageAnalyticsService.sendMixpanelEvent(data, false, mixpanelProperties, this.filterStatus);
    }
  }
  /**
   * This function verify the name of analysis by trim the initial and last spaces from the name.
   */
  public verifyAnalysisName(): boolean {
    if (this.response.analysis.name) {
      this.response.analysis.name = this.response.analysis.name.trim();
      return this.response.analysis.name.length !== 0;
    }
    return false;
  }
  /**
   * This function emits the state of the component
   */
  public closeDialog(): void {
    this.closeComponent.emit(true);
  }
}
